
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import { Schedule, Direction } from '../../types';
import { useScheduleGroups } from '../../contexts/ScheduleGroupContext';
import {
  Shield, Clock, Layers, Box, FileText, ArrowUpCircle, ArrowDownCircle,
  AlertTriangle, Crown, Moon, ShieldAlert, Wrench,
  LucideProps
} from 'lucide-react';

// Color map: tailwind color key → concrete class strings
const COLOR_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  slate:   { bg: 'bg-slate-50',   border: 'border-slate-100',  text: 'text-slate-500' },
  red:     { bg: 'bg-red-50',     border: 'border-red-100',    text: 'text-red-700' },
  purple:  { bg: 'bg-purple-50',  border: 'border-purple-100', text: 'text-purple-700' },
  amber:   { bg: 'bg-amber-50',   border: 'border-amber-100',  text: 'text-amber-700' },
  indigo:  { bg: 'bg-indigo-50',  border: 'border-indigo-100', text: 'text-indigo-700' },
  orange:  { bg: 'bg-orange-50',  border: 'border-orange-100', text: 'text-orange-700' },
  blue:    { bg: 'bg-blue-50',    border: 'border-blue-100',   text: 'text-blue-700' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100',text: 'text-emerald-700' },
  teal:    { bg: 'bg-teal-50',    border: 'border-teal-100',   text: 'text-teal-700' },
  pink:    { bg: 'bg-pink-50',    border: 'border-pink-100',   text: 'text-pink-700' },
};

const DEFAULT_COLOR = COLOR_STYLES.slate;

// Icon map: icon name → component
const ICON_MAP: Record<string, React.FC<LucideProps>> = {
  AlertTriangle,
  Crown,
  Clock,
  Moon,
  ShieldAlert,
  Wrench,
};

const ScheduleNodeComponent = ({ id, data, selected }: NodeProps<Schedule>) => {
  const { setNodes } = useReactFlow();
  const { groups } = useScheduleGroups();
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(data.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const isActive = data.isActive;

  // Find group definition
  const groupDef = useMemo(
    () => groups.find(g => g.name === data.group),
    [groups, data.group]
  );

  const colorStyle = (groupDef?.color && COLOR_STYLES[groupDef.color]) || DEFAULT_COLOR;
  const IconComponent = groupDef?.icon ? ICON_MAP[groupDef.icon] : undefined;

  // Sync state if data changes externally
  useEffect(() => {
    setEditedName(data.name);
  }, [data.name]);

  const onStartEdit = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setIsEditing(true);
    setEditedName(data.name);
  };

  const onCancel = () => {
    setIsEditing(false);
    setEditedName(data.name);
  };

  const onSave = () => {
    if (editedName.trim() && editedName !== data.name) {
        setNodes((nds) => nds.map(n => {
            if (n.id === id) {
                return { ...n, data: { ...n.data, name: editedName }};
            }
            return n;
        }));
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          onSave();
      }
      if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onCancel();
      }
  };

  // Focus logic
  useEffect(() => {
      if (isEditing && inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
      }
  }, [isEditing]);

  // Global hotkey logic for Ctrl+R
  useEffect(() => {
      if (!selected) return;
      const onGlobalKeyDown = (e: KeyboardEvent) => {
          if ((e.ctrlKey || e.metaKey) && e.key === 'r') {
              e.preventDefault();
              onStartEdit();
          }
      };
      window.addEventListener('keydown', onGlobalKeyDown);
      return () => window.removeEventListener('keydown', onGlobalKeyDown);
  }, [selected]);

  // Compute Effective Resources Count based on Priority Rules
  const effectiveResourceCount = useMemo(() => {
    const inherited = data._inheritedResources || [];
    const direct = data.resources;

    // Map to store the winner for each ResourceId
    const bestResources = new Map<string, number>();

    // 1. Process Direct Resources
    direct.forEach(r => {
        bestResources.set(r.resourceId, data.priority);
    });

    // 2. Process Inherited Resources
    inherited.forEach(r => {
        const currentBest = bestResources.get(r.resourceId);
        if (currentBest === undefined || r.sourceNodePriority > currentBest) {
            bestResources.set(r.resourceId, r.sourceNodePriority);
        }
    });

    return bestResources.size;
  }, [data.resources, data._inheritedResources, data.priority]);

  return (
    <div
      className={`relative w-[280px] shadow-sm rounded-xl border-2 transition-all duration-200 bg-white
      ${selected ? 'border-teal-500 ring-2 ring-teal-100' : 'border-slate-200'}
      ${!isActive ? 'opacity-70 grayscale' : ''}
      `}
    >
      <Handle type="target" position={Position.Top} className="!bg-slate-400 !w-3 !h-3" />

      {/* Header */}
      <div className={`px-4 py-2 rounded-t-xl flex items-center justify-between border-b ${colorStyle.bg} ${colorStyle.border}`}>
        <span className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1 ${colorStyle.text}`}>
          {IconComponent && <IconComponent size={12} />}
          {data.group}
        </span>
        <div className="flex gap-2 items-center">
            {data.direction === Direction.BottomUp && (
              <span title="Inherits from Parents" className="flex items-center">
                <ArrowUpCircle size={14} className="text-slate-400" />
              </span>
            )}
            {data.direction === Direction.TopDown && (
              <span title="Inherits from Children" className="flex items-center">
                <ArrowDownCircle size={14} className="text-slate-400" />
              </span>
            )}
            <span className="text-xs bg-white border border-slate-200 px-2 py-0.5 rounded-full text-slate-600 font-mono">
            P{data.priority}
            </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        {isEditing ? (
             <div className="mb-3">
                 <input
                    ref={inputRef}
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={onSave}
                    className="w-full text-sm font-semibold text-slate-800 border-2 border-teal-500 rounded p-1 focus:outline-none"
                    onClick={(e) => e.stopPropagation()}
                 />
             </div>
        ) : (
            <div
                className="font-semibold text-slate-800 text-sm mb-3 truncate flex items-center gap-2 cursor-text hover:bg-slate-50 rounded -ml-1 pl-1 py-0.5"
                title={`${data.name} (Double-click to edit)`}
                onDoubleClick={onStartEdit}
            >
            <Shield size={16} className="text-teal-600 flex-shrink-0" />
            {data.name}
            </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center text-xs text-slate-600 gap-2">
            <Clock size={14} className="text-slate-400" />
            <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700 font-mono">{data.time}</span>
          </div>

          <div className="flex items-center text-xs text-slate-600 gap-2" title="Purpose">
            <FileText size={14} className="text-slate-400" />
            <span className="truncate max-w-[180px]">{data.purpose}</span>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100 mt-2">
             <div className="flex items-center text-xs text-slate-500 gap-1.5" title="Effective Resources (After Priority Resolution)">
                <Box size={14} className="text-teal-600" />
                <span className="font-bold text-slate-700 font-mono">
                    {effectiveResourceCount}
                </span>
                <span className="text-[10px] text-slate-400 font-normal">effective</span>
             </div>
             {data.configuration?.workflow?.type && (
                <div className="flex items-center text-xs text-slate-400 gap-1" title="Workflow Type">
                    <Layers size={14} />
                    <span>Type {data.configuration.workflow.type}</span>
                </div>
             )}
          </div>
        </div>
    </div>

      <Handle type="source" position={Position.Bottom} className="!bg-slate-400 !w-3 !h-3" />
    </div>
  );
};

const ScheduleNode = React.memo(ScheduleNodeComponent);
export default ScheduleNode;
